import { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, TelegramLogo, LinkBreak, PaperPlaneTilt, Copy, Star, ArrowsClockwise, QrCode, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  portal_view: { emoji: '💼', label: 'Portal' },
  catalog_view: { emoji: '📦', label: 'Catálogo' },
  offers_view: { emoji: '🏷️', label: 'Ofertas' },
  shipments_toggle: { emoji: '🚚', label: 'Ver envíos' },
  history_toggle: { emoji: '📜', label: 'Ver ventas' },
};

function _fmtSecs(s) {
  const n = Math.max(0, Math.round(Number(s) || 0));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const ss = n % 60;
  return ss === 0 ? `${m}m` : `${m}m ${ss}s`;
}

function SellerActivityPanel({ unlocked, pinInput, setPinInput, onUnlock, data, loading }) {
  if (!unlocked) {
    return (
      <div className="bg-white border border-border rounded-lg p-8 max-w-md mx-auto">
        <div className="text-center mb-4">
          <div className="text-4xl mb-2">🔒</div>
          <h3 className="font-bold text-slate-800">Uso de herramientas</h3>
          <p className="text-sm text-slate-500 mt-1">Ingresa el NIP para ver las estadísticas</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onUnlock(); }} className="space-y-3">
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            className="w-full text-center text-2xl tracking-widest font-bold py-3 border-2 border-slate-200 focus:border-red-500 rounded-xl outline-none"
            placeholder="NIP"
            autoFocus
            data-testid="activity-pin-input"
          />
          <Button type="submit" className="w-full" data-testid="activity-pin-submit">Ver actividad</Button>
        </form>
      </div>
    );
  }
  if (loading || !data) {
    return <div className="text-center py-8 text-slate-500">Cargando…</div>;
  }
  const sellers = data.sellers || [];
  const range = data.range || {};
  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Del <b>{range.start}</b> al <b>{range.end}</b> — últimos 14 días. Se cuenta cada visita cuando el vendedor entra a esa sección con su dispositivo (dispositivo reclamado al iniciar sesión con NIP).
      </p>
      {sellers.length === 0 && (
        <div className="bg-white border border-border rounded-lg p-8 text-center text-slate-500">
          Aún no hay actividad registrada. Los vendedores deben iniciar sesión con su NIP al menos una vez para que su dispositivo empiece a registrar uso.
        </div>
      )}
      {sellers.length > 0 && (
        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="activity-table">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Vendedor</th>
                {Object.entries(EVENT_LABELS).map(([k, v]) => (
                  <th key={k} className="px-3 py-2 text-center text-xs font-semibold uppercase text-slate-600">
                    <div className="whitespace-nowrap">{v.emoji} {v.label}</div>
                    <div className="text-[10px] font-normal text-slate-400 normal-case">Veces · Prom.</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sellers.map((s) => (
                <tr key={s.seller_id} className="hover:bg-slate-50" data-testid={`activity-row-${s.seller_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                  {Object.keys(EVENT_LABELS).map((ev) => {
                    const t = s.totals[ev];
                    if (!t || t.count === 0) {
                      return <td key={ev} className="px-3 py-2 text-center text-slate-300">—</td>;
                    }
                    return (
                      <td key={ev} className="px-3 py-2 text-center">
                        <div className="font-bold text-slate-800 tabular-nums">{t.count}</div>
                        {t.avg_seconds > 0 && (
                          <div className="text-[11px] text-slate-500 tabular-nums">{_fmtSecs(t.avg_seconds)}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const Sellers = () => {
  const { sellers, refreshSellers } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [tab, setTab] = useState('list'); // 'list' | 'activity'
  const [activityUnlocked, setActivityUnlocked] = useState(false);
  const [activityPinInput, setActivityPinInput] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', qr_code: '', nip: '' });
  const [botUsername, setBotUsername] = useState(null);
  const [tgDialog, setTgDialog] = useState({ open: false, seller: null, code: null, loading: false });

  useEffect(() => {
    axios.get(`${API}/telegram/bot-info`, { withCredentials: true })
      .then((r) => setBotUsername(r.data.username))
      .catch(() => {});
  }, []);

  const fetchSellers = async () => {
    await refreshSellers();
  };

  const openDialog = (seller = null) => {
    if (seller) {
      setEditing(seller);
      setFormData({ name: seller.name, email: seller.email || '', phone: seller.phone || '', qr_code: seller.qr_code || '', nip: seller.nip || '' });
    } else {
      setEditing(null);
      setFormData({ name: '', email: '', phone: '', qr_code: '', nip: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validar NIP si viene
    const nipTrim = (formData.nip || '').trim();
    if (nipTrim && !/^\d{4}$/.test(nipTrim)) {
      toast.error('El NIP debe ser exactamente 4 dígitos');
      return;
    }
    try {
      if (editing) {
        const payload = { name: formData.name, email: formData.email, phone: formData.phone };
        if (isAdmin && !editing.is_local && formData.qr_code && formData.qr_code !== (editing.qr_code || '')) {
          payload.qr_code = formData.qr_code.trim();
        }
        // NIP: solo enviarlo si cambió (evita reescribir el mismo valor)
        if (nipTrim && nipTrim !== (editing.nip || '')) {
          payload.nip = nipTrim;
        }
        await axios.put(`${API}/sellers/${editing.id}`, payload, { withCredentials: true });
        toast.success('Vendedor actualizado');
      } else {
        const createPayload = { name: formData.name, email: formData.email, phone: formData.phone };
        if (nipTrim) createPayload.nip = nipTrim;
        await axios.post(`${API}/sellers`, createPayload, { withCredentials: true });
        toast.success('Vendedor creado');
      }
      setShowDialog(false);
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar vendedor');
    }
  };

  const handleDelete = async (seller) => {
    if (!window.confirm(`¿Eliminar al vendedor "${seller.name}"? Sus ventas históricas se conservan.`)) return;
    try {
      await axios.delete(`${API}/sellers/${seller.id}`, { withCredentials: true });
      toast.success('Vendedor eliminado');
      fetchSellers();
    } catch (error) {
      toast.error('Error al eliminar vendedor');
    }
  };

  const toggleApprover = async (seller) => {
    try {
      await axios.put(
        `${API}/sellers/${seller.id}`,
        { is_approver: !seller.is_approver },
        { withCredentials: true }
      );
      toast.success(seller.is_approver
        ? `${seller.name} ya no es aprobador`
        : `${seller.name} marcado como aprobador ⭐`);
      fetchSellers();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const toggleNotifyTransfers = async (seller) => {
    try {
      await axios.put(
        `${API}/sellers/${seller.id}`,
        { notify_transfers: !seller.notify_transfers },
        { withCredentials: true }
      );
      toast.success(seller.notify_transfers
        ? `${seller.name} ya no recibe notificaciones de transferencias`
        : `${seller.name} recibirá notificaciones de transferencias 📦`);
      fetchSellers();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const openTelegramDialog = (seller) => {
    setTgDialog({ open: true, seller, code: null, loading: false });
  };

  const generateCode = async () => {
    setTgDialog((d) => ({ ...d, loading: true }));
    try {
      const { data } = await axios.post(`${API}/sellers/${tgDialog.seller.id}/telegram/code`, {}, { withCredentials: true });
      setTgDialog((d) => ({ ...d, code: data.code, loading: false }));
      toast.success('Código generado');
    } catch (error) {
      toast.error('Error al generar código');
      setTgDialog((d) => ({ ...d, loading: false }));
    }
  };

  const sendTestMessage = async () => {
    try {
      await axios.post(`${API}/sellers/${tgDialog.seller.id}/telegram/test`, {}, { withCredentials: true });
      toast.success('Mensaje de prueba enviado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo enviar el mensaje');
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm('¿Desvincular Telegram de este vendedor? Dejará de recibir notificaciones.')) return;
    try {
      await axios.post(`${API}/sellers/${tgDialog.seller.id}/telegram/unlink`, {}, { withCredentials: true });
      toast.success('Telegram desvinculado');
      setTgDialog({ open: false, seller: null, code: null, loading: false });
      fetchSellers();
    } catch (error) {
      toast.error('Error al desvincular');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado');
  };

  const regenerateQr = async (sellerId) => {
    if (!window.confirm('¿Regenerar el código QR? El código anterior dejará de funcionar inmediatamente.')) return;
    try {
      const { data } = await axios.post(`${API}/sellers/${sellerId}/regenerate-qr`, {}, { withCredentials: true });
      toast.success('Código regenerado');
      refreshSellers();
      copyToClipboard(data.qr_code);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo regenerar');
    }
  };

  const regenerateNip = async (sellerId) => {
    if (!window.confirm('¿Regenerar el NIP del vendedor? El NIP anterior dejará de funcionar inmediatamente.')) return;
    try {
      const { data } = await axios.post(`${API}/sellers/${sellerId}/regenerate-nip`, {}, { withCredentials: true });
      toast.success(`Nuevo NIP: ${data.nip}`);
      refreshSellers();
      copyToClipboard(data.nip);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo regenerar');
    }
  };

  const startLink = botUsername && tgDialog.code
    ? `https://t.me/${botUsername}?start=${tgDialog.code}`
    : null;

  // Refrescar la info del seller en el dialog si fetchSellers actualiza
  const currentSellerInDialog = tgDialog.seller
    ? sellers.find((s) => s.id === tgDialog.seller.id) || tgDialog.seller
    : null;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full"><p>Cargando...</p></div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Vendedores</h1>
            <p className="text-muted-foreground mt-1">Gestiona tu equipo de ventas y sus notificaciones de Telegram</p>
          </div>
          <Button onClick={() => openDialog()} data-testid="add-seller-button">
            <Plus size={20} weight="bold" className="mr-2" />
            Nuevo Vendedor
          </Button>
        </div>

        {/* Tabs: Lista de vendedores | Uso de herramientas (admin only) */}
        {isAdmin && (
          <div className="flex gap-1 mb-4 border-b border-slate-200">
            <button
              onClick={() => setTab('list')}
              data-testid="tab-sellers-list"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                tab === 'list' ? 'border-red-600 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >Lista</button>
            <button
              onClick={() => setTab('activity')}
              data-testid="tab-sellers-activity"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition flex items-center gap-1.5 ${
                tab === 'activity' ? 'border-red-600 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            ><ChartBar size={16} weight="bold" /> Uso de herramientas</button>
          </div>
        )}

        {tab === 'activity' && isAdmin ? (
          <SellerActivityPanel
            unlocked={activityUnlocked}
            pinInput={activityPinInput}
            setPinInput={setActivityPinInput}
            onUnlock={async () => {
              if (activityPinInput !== '0256') {
                toast.error('NIP incorrecto');
                return;
              }
              setActivityUnlocked(true);
              setActivityLoading(true);
              try {
                const { data } = await axios.get(`${API}/admin/seller-activity`, { withCredentials: true });
                setActivityData(data);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'No se pudo cargar');
              } finally {
                setActivityLoading(false);
              }
            }}
            data={activityData}
            loading={activityLoading}
          />
        ) : (

        <div className="bg-white border border-border rounded-sm">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sellers-table">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Teléfono</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Telegram</th>
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">NIP</th>
                  )}
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Código QR</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sellers.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 btn-transition" data-testid={`seller-row-${s.id}`}>
                    <td className="px-4 py-3 text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {s.name}
                        {s.is_approver && (
                          <span title="Aprobador" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 border border-amber-300">
                            <Star size={10} weight="fill" /> APROBADOR
                          </span>
                        )}
                        {s.notify_transfers && (
                          <span title="Recibe notificaciones de transferencias por Telegram" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-700 border border-sky-300">
                            📦 TRANSFERENCIAS
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{s.email || '-'}</td>
                    <td className="px-4 py-3 text-sm">{s.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {s.telegram_chat_id ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                          <TelegramLogo size={12} weight="fill" />
                          Vinculado
                          {s.telegram_username && <span className="text-emerald-600">· @{s.telegram_username}</span>}
                        </span>
                      ) : s.telegram_link_code ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-300">
                          Pendiente
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No vinculado</span>
                      )}
                    </td>
                    {isAdmin && (
                    <td className="px-4 py-3 text-sm" data-testid={`seller-nip-cell-${s.id}`}>
                      {s.is_local ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : s.nip ? (
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono font-bold bg-red-50 text-red-700 border border-red-200 px-2 py-1 rounded select-all tracking-widest">{s.nip}</code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(s.nip)}
                            title="Copiar NIP"
                            className="p-1 hover:bg-slate-100 rounded"
                            data-testid={`seller-nip-copy-${s.id}`}>
                            <Copy size={14} />
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => regenerateNip(s.id)}
                              title="Regenerar NIP"
                              className="p-1 hover:bg-amber-100 rounded text-amber-600"
                              data-testid={`seller-nip-regen-${s.id}`}>
                              <ArrowsClockwise size={14} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm" data-testid={`seller-qr-cell-${s.id}`}>
                        {s.qr_code ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded select-all">{s.qr_code}</code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(s.qr_code)}
                              title="Copiar"
                              className="p-1 hover:bg-slate-100 rounded"
                              data-testid={`seller-qr-copy-${s.id}`}>
                              <Copy size={14} />
                            </button>
                            {!s.is_local && (
                              <button
                                type="button"
                                onClick={() => regenerateQr(s.id)}
                                title="Regenerar"
                                className="p-1 hover:bg-amber-100 rounded text-amber-600"
                                data-testid={`seller-qr-regen-${s.id}`}>
                                <ArrowsClockwise size={14} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleApprover(s)}
                          data-testid={`approver-toggle-${s.id}`}
                          title={s.is_approver ? 'Quitar como aprobador' : 'Marcar como aprobador'}
                        >
                          <Star size={14} weight={s.is_approver ? 'fill' : 'regular'} className={s.is_approver ? 'text-amber-500' : ''} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleNotifyTransfers(s)}
                          data-testid={`notify-transfers-toggle-${s.id}`}
                          title={s.notify_transfers ? 'Dejar de notificar transferencias' : 'Notificar transferencias por Telegram'}
                          className={s.notify_transfers ? 'bg-sky-50 border-sky-300 text-sky-700' : ''}
                        >
                          📦
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openTelegramDialog(s)}
                          data-testid={`telegram-seller-${s.id}`}
                          title="Configurar Telegram"
                        >
                          <TelegramLogo size={14} weight={s.telegram_chat_id ? 'fill' : 'regular'} className={s.telegram_chat_id ? 'text-blue-500' : ''} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openDialog(s)} data-testid={`edit-seller-${s.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(s)} data-testid={`delete-seller-${s.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sellers.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">No hay vendedores registrados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {/* Edit/Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Vendedor' : 'Nuevo Vendedor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="seller-form">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="seller-name-input" required />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="seller-email-input" />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="seller-phone-input" />
            </div>
            {!editing?.is_local && (
              <div>
                <Label htmlFor="seller_nip" className="flex items-center gap-2">
                  NIP (4 dígitos)
                  <span className="text-[10px] text-slate-500 font-normal">— para portal <code>/vendedor</code></span>
                </Label>
                <Input
                  id="seller_nip"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder={editing?.nip || 'Se autogenera si lo dejas vacío'}
                  value={formData.nip}
                  onChange={(e) => setFormData({ ...formData, nip: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  data-testid="seller-nip-input"
                  className="font-mono tracking-widest"
                />
                <p className="text-xs text-slate-500 mt-1">Debe ser único entre vendedores. Si lo dejas vacío al crear, se genera automático.</p>
              </div>
            )}
            {isAdmin && editing && !editing.is_local && (
              <div>
                <Label htmlFor="qr_code" className="flex items-center gap-2">
                  Código QR
                  <span className="text-[10px] text-slate-500 font-normal">
                    (letras, números y símbolos — 4-64 caracteres, único)
                  </span>
                </Label>
                <Input
                  id="qr_code"
                  value={formData.qr_code}
                  onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
                  data-testid="seller-qr-input"
                  placeholder="Ej. VEND-URIEL-2026 o pegar contenido escaneado"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Este código se genera automáticamente al crear el vendedor, pero puedes personalizarlo aquí. Se guardará al presionar Actualizar.
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-seller-button">Cancelar</Button>
              <Button type="submit" data-testid="save-seller-button">{editing ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Telegram dialog */}
      <Dialog open={tgDialog.open} onOpenChange={(o) => !o && setTgDialog({ open: false, seller: null, code: null, loading: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TelegramLogo size={22} weight="fill" className="text-blue-500" />
              Telegram — {currentSellerInDialog?.name}
            </DialogTitle>
          </DialogHeader>

          {!botUsername && (
            <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded p-3">
              ⚠️ El bot de Telegram no está configurado. Verifica <code>TELEGRAM_BOT_TOKEN</code> en el backend.
            </div>
          )}

          {currentSellerInDialog?.telegram_chat_id ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm">
                <p className="font-medium text-emerald-900">✅ Vinculado correctamente</p>
                {currentSellerInDialog.telegram_username && (
                  <p className="text-emerald-700 mt-1">Usuario: @{currentSellerInDialog.telegram_username}</p>
                )}
                <p className="text-xs text-emerald-700 mt-1">chat_id: {currentSellerInDialog.telegram_chat_id}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={sendTestMessage} className="flex-1" data-testid="tg-test-message-button">
                  <PaperPlaneTilt size={16} className="mr-1" />
                  Enviar mensaje de prueba
                </Button>
                <Button onClick={unlinkTelegram} variant="destructive" data-testid="tg-unlink-button">
                  <LinkBreak size={16} className="mr-1" />
                  Desvincular
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Genera un código y compártelo con el vendedor. Al hacer clic en el enlace, Telegram se abrirá y la cuenta quedará vinculada automáticamente.
              </p>

              {!tgDialog.code ? (
                <Button onClick={generateCode} disabled={tgDialog.loading || !botUsername} className="w-full" data-testid="tg-generate-code-button">
                  {tgDialog.loading ? 'Generando...' : 'Generar código de vinculación'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-slate-50 border rounded p-3 space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Código</Label>
                      <div className="flex items-center gap-2">
                        <code className="text-2xl font-mono font-bold tracking-widest flex-1">{tgDialog.code}</code>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(tgDialog.code)}>
                          <Copy size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {startLink && (
                    <div className="bg-blue-50 border border-blue-300 rounded p-3 space-y-2">
                      <p className="text-sm font-medium text-blue-900">Enlace mágico (clic = vinculado):</p>
                      <a
                        href={startLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-blue-500 hover:bg-blue-600 text-white text-center font-medium py-2 rounded"
                        data-testid="tg-open-link"
                      >
                        Abrir en Telegram
                      </a>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={startLink}
                          className="flex-1 text-xs bg-white border rounded px-2 py-1 font-mono"
                        />
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(startLink)}>
                          <Copy size={14} />
                        </Button>
                      </div>
                      <p className="text-xs text-blue-700">
                        Comparte este enlace por WhatsApp con el vendedor. Cuando lo abra, el bot lo vinculará al instante.
                      </p>
                    </div>
                  )}

                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">Vinculación manual (alternativa)</summary>
                    <div className="mt-2 pl-2 space-y-1">
                      <p>Si el enlace no funciona, el vendedor puede:</p>
                      <ol className="list-decimal list-inside space-y-0.5">
                        <li>Buscar <code className="bg-slate-100 px-1">@{botUsername}</code> en Telegram.</li>
                        <li>Enviar: <code className="bg-slate-100 px-1">/vincular {tgDialog.code}</code></li>
                      </ol>
                    </div>
                  </details>

                  <Button onClick={fetchSellers} variant="outline" className="w-full" data-testid="tg-refresh-status-button">
                    Verificar estado de vinculación
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
